"use client";

import { Paths, pathToEditCatalog } from "@app/routes";
import {
  AppContentFooter,
  type IFormBuilderSchema,
  useFormBuilderTrigger,
} from "@atlas/composite";
import {
  Button,
  type FileMetadata,
  Form,
  StepperWizard,
  useToast,
} from "@atlas/core";
import {
  type IFormBuilder,
  useCreateFormBuilder,
  type useTranslation,
  useUpdateFormBuilder,
} from "@atlas/sdk";
import { IHubCatalogStatus, IHubCatalogVisibility } from "@enums";
import type { IHubCatalog, IHubCatalogType } from "@models";
import { CatalogInfoWrapper, CatalogMode } from "@modules";
import { useCreateHubCatalog, useUpdateHubCatalog, useUserInfo } from "@query";
import { blobUrlToBase64, fileToBase64, useBreadcrumbContext } from "@shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type FieldErrors, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import {
  CatalogDetailsForm,
  type ICatalogDetailsFormValues,
} from "./CatalogDetailsForm";
import { SubmitForReviewDialog } from "./SubmitForReviewDialog";
import { UserIntegrationTuningStep } from "./UserIntegrationTuningStep";

const MINIMAL_FORM_CONFIG = '{"fields":[],"layout":[],"layoutRows":[]}';

function typeLabelForSelectedCatalogType(
  catalogTypes: IHubCatalogType[],
  typeId: number,
): string {
  const ct = catalogTypes.find((c) => (c.typeId ?? 0) === typeId);
  return (ct?.label ?? ct?.name ?? "").trim();
}

async function loadCatalogIconBase64(
  values: ICatalogDetailsFormValues,
): Promise<string> {
  try {
    const iconField = values.icon as unknown as FileMetadata | FileMetadata[];
    if (!iconField) return "";

    const iconFiles = Array.isArray(iconField) ? iconField : [iconField];
    const file = iconFiles[0];
    if (!file) return "";

    if (file instanceof File || file instanceof Blob) {
      return fileToBase64(file);
    }

    if (
      "file" in file &&
      (file.file instanceof File || file.file instanceof Blob)
    ) {
      return fileToBase64(file.file);
    }

    if (typeof file.url === "string" && file.url) {
      const dataUrl = await blobUrlToBase64(file.url);
      const parts = dataUrl.split(",");
      return parts.length > 1 ? parts[1] : dataUrl;
    }

    if (typeof file === "string") {
      const fileStr = file as string;
      const parts = fileStr.split(",");
      return parts.length > 1 ? parts[1] : fileStr;
    }
  } catch (err) {
    console.error("Failed to load catalog icon", err);
  }
  return "";
}

export type CreateEditCatalogWizardProps = {
  isEdit: boolean;
  numericCatalogId: number;
  mode: CatalogMode;
  catalog: IHubCatalog | undefined;
  existingFormData: IFormBuilder | undefined;
  catalogTypes: IHubCatalogType[];
  tierOptions: { value: string; label: string }[];
  cloudOptions: { value: string; label: string }[];
  isCatalogTypesLoading: boolean;
  isCdvLoading: boolean;
  formDefaultValues: ICatalogDetailsFormValues;
  catalogFormValues: ICatalogDetailsFormValues | undefined;
  t: ReturnType<typeof useTranslation>["t"];
};

export function CreateEditCatalogWizard({
  isEdit,
  numericCatalogId,
  mode,
  catalog,
  existingFormData,
  catalogTypes,
  tierOptions,
  cloudOptions,
  isCatalogTypesLoading,
  isCdvLoading,
  formDefaultValues,
  catalogFormValues,
  t,
}: CreateEditCatalogWizardProps) {
  const { mutateAsync: createCatalogAsync, isPending: isCreateCatalogPending } =
    useCreateHubCatalog();
  const { mutateAsync: updateCatalogAsync, isPending: isUpdateCatalogPending } =
    useUpdateHubCatalog();
  const { mutateAsync: createFormAsync, isPending: isCreateFormPending } =
    useCreateFormBuilder();
  const { mutateAsync: updateFormAsync, isPending: isUpdateFormPending } =
    useUpdateFormBuilder();

  const { data: userInfo } = useUserInfo();
  const companyId = userInfo?.companyId ?? 0;

  const isSubmitting = useMemo(
    () =>
      isCreateCatalogPending ||
      isUpdateCatalogPending ||
      isCreateFormPending ||
      isUpdateFormPending,
    [
      isCreateCatalogPending,
      isUpdateCatalogPending,
      isCreateFormPending,
      isUpdateFormPending,
    ],
  );

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { setContributeCatalogLabel } = useBreadcrumbContext();

  const [formSchema, setFormSchema] = useState<IFormBuilderSchema | null>(null);
  const [generatedFormJson, setGeneratedFormJson] = useState<string>("");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submitForReviewOpen, setSubmitForReviewOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [isAutosaved, setIsAutosaved] = useState(false);
  const [formBuilderRef, submitForm] = useFormBuilderTrigger();

  const form = useForm<ICatalogDetailsFormValues>({
    defaultValues: formDefaultValues,
    values: catalogFormValues ?? formDefaultValues,
    resetOptions: {
      keepDirtyValues: true,
    },

    mode: "onChange",
    shouldFocusError: true,
  });
  const { handleSubmit, getValues, setValue } = form;

  const contributeLabelWatch = form.watch("label");
  const previewFormValues = useWatch({ control: form.control });
  useEffect(() => {
    if (isEdit) setContributeCatalogLabel("");
    else setContributeCatalogLabel(contributeLabelWatch.trim());
    return () => setContributeCatalogLabel("");
  }, [isEdit, contributeLabelWatch, setContributeCatalogLabel]);

  const handleSchemaChange = useCallback(
    (
      schema: IFormBuilderSchema | null,
      generatedJson?: string,
      isDirty?: boolean,
    ) => {
      setFormSchema(schema);
      setGeneratedFormJson(
        generatedJson ?? (schema ? JSON.stringify(schema, null, 2) : ""),
      );
      if (isDirty !== undefined) {
        setIsFormDirty(isDirty);
      }
    },
    [],
  );
  const handleNext = useCallback(async () => {
    if (wizardStep === 1) {
      const valid = await form.trigger();
      return valid;
    }
    return true;
  }, [wizardStep, form]);

  const completedSteps = useMemo(() => {
    const steps: number[] = [];
    if (form.formState.isValid) steps.push(1);

    const hasFormContent =
      formSchema &&
      ((formSchema.fields && formSchema.fields.length > 0) ||
        (formSchema.layoutRows && formSchema.layoutRows.length > 0));
    if (hasFormContent || wizardStep > 2) steps.push(2);

    return steps;
  }, [form, formSchema, wizardStep]);

  const isStep1Dirty = useMemo(() => {
    return (
      form.formState.isDirty &&
      Object.keys(form.formState.dirtyFields).filter((k) => k !== "addon")
        .length > 0
    );
  }, [form.formState.isDirty, form.formState.dirtyFields]);

  const previewCatalog = useMemo((): IHubCatalog | null => {
    if (isEdit || wizardStep !== 3) return null;
    const values = previewFormValues;
    if (!values) return null;
    const typeIdNum = values.typeId ? parseInt(values.typeId, 10) : 0;
    const tierIdNum = values.tierId ? parseInt(values.tierId, 10) : 0;
    const iconField = values.icon as unknown as FileMetadata | FileMetadata[];
    const iconFiles = Array.isArray(iconField) ? iconField : [iconField];
    const iconUrl =
      iconFiles[0]?.url ??
      (typeof iconFiles[0] === "string" ? iconFiles[0] : "");
    const documentationLocations = (values.documentationLocations ?? [])
      .map((v) => String(v).trim())
      .filter(Boolean);
    const parentCatalogIds = (values.parentCatalogIds ?? [])
      .map((id) => parseInt(id, 10))
      .filter((n) => Number.isFinite(n));
    const catalogTypeLabel = typeLabelForSelectedCatalogType(
      catalogTypes,
      typeIdNum,
    );
    return {
      typeId: typeIdNum,
      catalogTypeLabel,
      name: values.label?.trim() || "draft",
      label: values.label ?? "",
      description: values.description ?? "",
      token: values.token ?? "",
      overview: values.overview ?? "",
      supportedCloud: values.supportedCloud ?? [],
      documentationLocations,
      parentCatalogIds,
      tierId: tierIdNum,
      addon: values.addon ?? false,
      featured: values.featured ?? false,
      status: IHubCatalogStatus.Draft,
      media: [],
      icon: iconUrl,
      formId: 0,
    };
  }, [catalogTypes, isEdit, wizardStep, previewFormValues]);

  const resolveFormConfigJson = useCallback(
    (override?: string) =>
      override ??
      (generatedFormJson ||
        (formSchema ? JSON.stringify(formSchema, null, 2) : "")),
    [generatedFormJson, formSchema],
  );

  const runPersist = useCallback(
    async (args: {
      values: ICatalogDetailsFormValues;
      formConfigJson: string;
      reviewSubmitTarget?: string;
      visibility?: IHubCatalogVisibility;
    }) => {
      const { values, formConfigJson, reviewSubmitTarget, visibility } = args;
      const reviewTarget = reviewSubmitTarget?.trim() ?? "";
      const isSubmitFlow = reviewTarget.length > 0;

      if (!formConfigJson.trim()) {
        toast.add({
          title: t("hub.create_catalog.form_required", {
            defaultValue: "Form configuration required",
          }),
          description: t("hub.create_catalog.build_form_first", {
            defaultValue:
              "Please configure the integration form before continuing.",
          }),
          data: { type: "error" },
        });
        return false;
      }

      const documentationLocations = (values.documentationLocations ?? [])
        .map((v) => String(v).trim())
        .filter(Boolean);
      const parentCatalogIds = (values.parentCatalogIds ?? []).map((id) =>
        parseInt(id, 10),
      );
      const typeIdNum = parseInt(values.typeId, 10) || 0;
      const tierIdNum = parseInt(values.tierId, 10) || 0;
      const icon = await loadCatalogIconBase64(values);

      const navigateAfterPersist = (saved: IHubCatalog) => {
        if (isSubmitFlow) {
          navigate(`/${Paths.CatalogManager}?tab=myCatalogs`);
          return;
        }
        const id = saved.catalogId ?? catalog?.catalogId;
        if (id != null) {
          navigate(
            pathToEditCatalog(id, String(saved.version || catalog?.version)),
            { state: { isNew: !isEdit || location.state?.isNew } },
          );
        } else {
          navigate(`/${Paths.CatalogManager}`);
        }
      };

      try {
        let saved: IHubCatalog;

        if (catalog?.formId && catalog.formId > 0) {
          await updateFormAsync({
            formId: catalog.formId,
            formConfig: formConfigJson,
            isActive: true,
          });

          const { version, ...catalogWithoutVersion } = catalog;
          const baseCatalogPayload =
            catalog.status === IHubCatalogStatus.Approved
              ? { ...catalogWithoutVersion, status: IHubCatalogStatus.Draft }
              : catalog;

          saved = await updateCatalogAsync({
            ...baseCatalogPayload,
            typeId: typeIdNum,
            name: catalog.name ?? (values.label?.trim() || ""),
            label: values.label,
            description: values.description ?? "",
            token: values.token ?? "",
            overview: values.overview ?? "",
            supportedCloud: values.supportedCloud ?? [],
            documentationLocations,
            parentCatalogIds,
            tierId: tierIdNum,
            addon: values.addon ?? false,
            featured: values.featured ?? false,
            media: [],
            icon,
            visibility:
              isSubmitFlow && visibility !== undefined
                ? visibility
                : IHubCatalogVisibility.Private,
            status:
              isSubmitFlow && visibility !== undefined
                ? visibility === IHubCatalogVisibility.Private ||
                  visibility === IHubCatalogVisibility.Company
                  ? IHubCatalogStatus.Submitted
                  : IHubCatalogStatus.Review
                : IHubCatalogStatus.Draft,
          });
        } else {
          const newForm = await createFormAsync({
            formConfig: formConfigJson,
            isActive: true,
          });
          saved = await createCatalogAsync({
            companyId,
            typeId: typeIdNum,
            name: values.label?.trim() || "draft",
            label: values.label,
            description: values.description ?? "",
            token: values.token ?? "",
            overview: values.overview ?? "",
            supportedCloud: values.supportedCloud ?? [],
            documentationLocations,
            parentCatalogIds,
            tierId: tierIdNum,
            addon: values.addon ?? false,
            featured: values.featured ?? false,
            formId: newForm.formId,
            status:
              isSubmitFlow && visibility !== undefined
                ? visibility === IHubCatalogVisibility.Private ||
                  visibility === IHubCatalogVisibility.Company
                  ? IHubCatalogStatus.Submitted
                  : IHubCatalogStatus.Review
                : IHubCatalogStatus.Draft,
            media: [],
            icon,
            visibility:
              isSubmitFlow && visibility !== undefined
                ? visibility
                : IHubCatalogVisibility.Private,
          });
        }

        setIsAutosaved(true);

        if (isSubmitFlow) {
          const isActuallyNew = !isEdit || location.state?.isNew;
          toast.add({
            title: isActuallyNew
              ? t("hub.create_catalog.create_success", {
                  defaultValue: "Catalog created successfully",
                })
              : t("hub.create_catalog.update_success", {
                  defaultValue: "Catalog updated successfully",
                }),
            data: { type: "success" },
          });
        }

        navigateAfterPersist(saved);
        return true;
      } catch (error) {
        const apiError = error as { status?: number; message?: string };
        toast.add({
          title: isSubmitFlow
            ? t("hub.create_catalog.submit_flow_error", {
                defaultValue: "Submit failed",
              })
            : t("hub.create_catalog.save_error", {
                defaultValue: "Failed to save catalog",
              }),
          ...(apiError.status === 409 && apiError.message
            ? { description: apiError.message }
            : isSubmitFlow
              ? {
                  description: t(
                    "hub.create_catalog.submit_flow_error_description",
                    {
                      defaultValue:
                        "Could not complete save and review. Please try again.",
                    },
                  ),
                }
              : {}),
          data: { type: "error" },
        });
        return false;
      }
    },
    [
      catalog,
      catalogTypes,
      createCatalogAsync,
      createFormAsync,
      isEdit,
      navigate,
      toast,
      t,
      updateCatalogAsync,
      updateFormAsync,
    ],
  );

  const onError = (errors: FieldErrors) => {
    const firstError = Object.keys(errors)[0];
    const selector = `[name="${CSS.escape(firstError)}"]`;
    const el = document.querySelector(selector);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      (el as HTMLElement).focus();
    }
  };

  const handleWizardSubmit = useCallback(() => {
    setSubmitForReviewOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (wizardStep === 1) {
      const valid = await form.trigger();
      if (!valid) return false;
      if (isEdit) {
        if (!existingFormData?.formConfig) {
          toast.add({
            title: t("hub.create_catalog.form_loading", {
              defaultValue: "Form loading",
            }),
            description: t("hub.create_catalog.please_wait", {
              defaultValue: "Please wait for the form to load.",
            }),
            data: { type: "error" },
          });
          return false;
        }
        return await runPersist({
          values: getValues(),
          formConfigJson: existingFormData.formConfig,
        });
      } else {
        return await runPersist({
          values: getValues(),
          formConfigJson: MINIMAL_FORM_CONFIG,
        });
      }
    } else {
      const formConfigJson =
        resolveFormConfigJson().trim() || MINIMAL_FORM_CONFIG;
      return await runPersist({
        values: getValues(),
        formConfigJson,
      });
    }
  }, [
    wizardStep,
    form,
    isEdit,
    existingFormData,
    getValues,
    resolveFormConfigJson,
    runPersist,
    toast,
    t,
  ]);

  const steps = useMemo(
    () => [
      {
        title: t("hub.header.catalog", {
          defaultValue: "Catalog",
        }),
        description: t("hub.create_catalog.step_catalog_desc", {
          defaultValue: "Configure catalog details",
        }),
      },
      {
        title: t("hub.create_catalog.step_user_integration", {
          defaultValue: "User Integration & Tuning",
        }),
        description: t("hub.create_catalog.step_user_integration_desc", {
          defaultValue: "Integration and tuning settings",
        }),
      },
      {
        title: t("hub.create_catalog.step_preview", {
          defaultValue: "Preview & Submit",
        }),
        description: t("hub.create_catalog.step_preview_desc", {
          defaultValue: "Review and submit",
        }),
      },
    ],
    [t],
  );

  const handleDialogSubmit = useCallback(
    (visibility: IHubCatalogVisibility) => {
      void runPersist({
        values: getValues(),
        formConfigJson: resolveFormConfigJson(),
        reviewSubmitTarget: "0",
        visibility,
      });
    },
    [runPersist, getValues, resolveFormConfigJson],
  );

  const catalogPageFooter = useMemo(() => {
    const totalStepCount = steps.length;
    const isLastStep = wizardStep >= totalStepCount;
    const goNext = () => {
      setWizardStep((w) => Math.min(w + 1, totalStepCount));
    };
    return (
      <div className="flex w-full items-center justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate(`/${Paths.CatalogManager}`)}
          disabled={isSubmitting}>
          {t("hub.administer.cancel", { defaultValue: "Cancel" })}
        </Button>

        {isLastStep ? (
          <Button
            type="button"
            onClick={handleWizardSubmit}
            disabled={isSubmitting}>
            {t("hub.create_catalog.submit", { defaultValue: "Submit" })}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={async () => {
              const isFormBuilderStep = wizardStep === totalStepCount - 1;
              if (isFormBuilderStep) {
                const { valid } = submitForm();
                if (valid) {
                  await handleSubmit(async () => {
                    const saved = await handleSave();
                    if (saved) {
                      const validNext = await handleNext();
                      if (validNext) {
                        goNext();
                      }
                    }
                  }, onError)();
                }
              } else {
                await handleSubmit(async () => {
                  const saved = await handleSave();
                  if (saved) {
                    const validNext = await handleNext();
                    if (validNext) {
                      goNext();
                    }
                  }
                }, onError)();
              }
            }}
            disabled={isSubmitting}>
            {t("auth.login.next", { defaultValue: "Next" })}
          </Button>
        )}
      </div>
    );
  }, [
    t,
    navigate,
    handleWizardSubmit,
    handleSave,
    handleNext,
    handleSubmit,
    submitForm,
    onError,
    steps,
    wizardStep,
    isSubmitting,
  ]);

  return (
    <StepperWizard
      orientation="vertical"
      value={wizardStep}
      onValueChange={setWizardStep}
      completedSteps={completedSteps}
      steps={steps}
      isSubmitting={isSubmitting}
      progressLabel={t("hub.create_catalog.progress", {
        defaultValue: "Progress",
      })}
      isAutosaved={isAutosaved}
      onAutosaveComplete={() => setIsAutosaved(false)}
      autosavedLabel={t("hub.create_catalog.autosaved", {
        defaultValue: "Autosaved",
      })}>
      <AppContentFooter>{catalogPageFooter}</AppContentFooter>
      <Form {...form}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <StepperWizard.Step value={1} dirty={isStep1Dirty}>
            <CatalogDetailsForm
              isLoading={isCatalogTypesLoading || isCdvLoading}
              control={form.control}
              setValue={setValue}
              catalogTypes={catalogTypes}
              tierOptions={tierOptions}
              cloudOptions={cloudOptions}
              excludeCatalogId={
                mode === CatalogMode.Edit && numericCatalogId > 0
                  ? numericCatalogId
                  : undefined
              }
            />
          </StepperWizard.Step>
          <StepperWizard.Step
            value={2}
            forceMount
            disableScroll
            dirty={isFormDirty}>
            <UserIntegrationTuningStep
              catalog={catalog}
              isEdit={isEdit}
              onSchemaChange={handleSchemaChange}
              formBuilderRef={formBuilderRef}
              onSubmit={() => setWizardStep(3)}
            />
          </StepperWizard.Step>
          <StepperWizard.Step value={3}>
            <CatalogInfoWrapper
              mode={mode}
              previewCatalog={isEdit ? null : previewCatalog}
            />
          </StepperWizard.Step>
        </div>
      </Form>
      <SubmitForReviewDialog
        open={submitForReviewOpen}
        onOpenChange={setSubmitForReviewOpen}
        onCancel={() => {}}
        onSubmit={handleDialogSubmit}
        catalogName={
          contributeLabelWatch.trim() || catalog?.label?.trim() || ""
        }
      />
    </StepperWizard>
  );
}
